import { EventRecordTypeEnum } from '@/constants/enums/eventRecordType.enums'
import type { MachineEventRecordV2Type } from '@/models/types/machine.types'

type V1Record = {
  date: string
  event?: string
  eventLog?: string
  eventCode: number
}

// isNewApi 是 Sidebar 上可即時切換的 runtime 旗標，V1 不能拔掉。與其讓畫面維護兩套
// 標題／描述／圖示／篩選邏輯，不如在這裡把 V1 攤平成 V2 的 { date, type, payload }，
// 下游就只認得一套 type（同檔案的 getCoinOperationsDetailQueryOptions 正規化
// exchangeCount／exchangedCount 也是同一個理由）。
const toNumber = (value?: string): number | null => {
  if (value === undefined || value.trim() === '') return null
  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

const toPayload: Record<
  number,
  (record: V1Record) => { type: EventRecordTypeEnum; payload: Record<string, unknown> }
> = {
  100: (r) => ({
    type: EventRecordTypeEnum.prizeDispense,
    payload: { accumulatedAmount: toNumber(r.eventLog) }
  }),
  110: (r) => ({
    type: EventRecordTypeEnum.cashboxCollection,
    payload: { amount: toNumber(r.eventLog) }
  }),
  111: (r) => ({
    type: EventRecordTypeEnum.remoteCoinInsert,
    payload: { amount: toNumber(r.eventLog) }
  }),
  112: () => ({ type: EventRecordTypeEnum.remotePrizeDispense, payload: {} }),
  101: (r) => ({
    type: EventRecordTypeEnum.coinExchange,
    payload: { coins: toNumber(r.eventLog) }
  }),
  210: (r) => ({
    type: EventRecordTypeEnum.remoteCoinDispense,
    payload: { coins: toNumber(r.eventLog) }
  }),
  214: (r) => ({
    type: EventRecordTypeEnum.stockAdjustment,
    payload: { stockChangeCoins: toNumber(r.eventLog) }
  }),
  215: () => ({ type: EventRecordTypeEnum.payoutReset, payload: {} }),
  211: () => ({ type: EventRecordTypeEnum.remoteUnlockWindow, payload: {} }),
  212: () => ({ type: EventRecordTypeEnum.remoteUnlock, payload: {} }),
  213: () => ({ type: EventRecordTypeEnum.remoteRestart, payload: {} }),
  // eventLog 是韌體送來的「變化前」門狀態，與 V2 的 payload.value 同義
  501: (r) => ({
    type: EventRecordTypeEnum.doorOpenedOrClosed,
    payload: { value: toNumber(r.eventLog) }
  }),
  0: () => ({ type: EventRecordTypeEnum.connected, payload: {} }),
  [-1]: () => ({ type: EventRecordTypeEnum.connectionTimeout, payload: {} }),
  // 以下三個 V2 已移除，只有 V1 會送；標題與描述沿用舊規則，所以 102 要留住原始文字
  102: (r) => ({
    type: EventRecordTypeEnum.legacyStockReport,
    payload: { event: r.event ?? '', eventLog: r.eventLog ?? '' }
  }),
  103: () => ({ type: EventRecordTypeEnum.legacyStatusReport, payload: {} }),
  4: () => ({ type: EventRecordTypeEnum.legacyMachineMalfunction, payload: {} })
}

export const normalizeV1EventRecords = (records?: V1Record[]): MachineEventRecordV2Type[] =>
  (records ?? []).flatMap((record) => {
    const mapper = toPayload[record.eventCode]
    if (!mapper) return []

    return [{ date: record.date, ...mapper(record) } as MachineEventRecordV2Type]
  })
